import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity, BarChart3, CalendarClock, ChevronLeft, ChevronRight, CircleUserRound,
  CreditCard, FileImage, Gauge, Layers3, LayoutDashboard, Library, ListVideo,
  LogOut, Menu, MonitorPlay, Settings, ShieldCheck, Sparkles, Users, WalletCards,
  X, Zap
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { db } from '../lib/firebase';
import Badge from './Badge';
import { screenLimitLabel } from '../utils/format';

const tenantNav = [
  ['Dashboard', '/tenant/dashboard', LayoutDashboard],
  ['Screens', '/tenant/screens', MonitorPlay],
  ['Media', '/tenant/media', Library],
  ['Playlists', '/tenant/playlists', ListVideo],
  ['Layouts', '/tenant/layouts', Layers3],
  ['Scheduler', '/tenant/scheduler', CalendarClock],
  ['Widgets', '/tenant/widgets', Sparkles],
  ['Analytics', '/tenant/analytics', BarChart3],
  ['Team', '/tenant/users', Users],
  ['Subscription', '/tenant/subscription', CreditCard],
  ['Settings', '/tenant/settings', Settings]
];

const superNav = [
  ['Overview', '/superadmin/dashboard', Gauge],
  ['Companies', '/superadmin/companies', MonitorPlay],
  ['Plans', '/superadmin/plans', WalletCards],
  ['Subscriptions', '/superadmin/subscriptions', CreditCard],
  ['Payment Gateways', '/superadmin/payment-gateways', WalletCards],
  ['Platform Users', '/superadmin/users', ShieldCheck],
  ['Settings', '/superadmin/settings', Settings]
];

export default function AppShell({ mode = 'tenant' }) {
  const { profile, tenant, subscription, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [platformLogo, setPlatformLogo] = useState('');
  const nav = mode === 'super' ? superNav : tenantNav;

  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    if (mode !== 'super') return;
    getDoc(doc(db, 'system_settings', 'config'))
      .then(snap => setPlatformLogo(snap.exists() ? (snap.data().logoUrl || '') : ''))
      .catch(() => setPlatformLogo(''));
  }, [mode, location.pathname]);
  useEffect(() => {
    if (mode !== 'super') return undefined;
    const onBranding = event => setPlatformLogo(event.detail?.logoUrl || '');
    window.addEventListener('weilink-platform-branding', onBranding);
    return () => window.removeEventListener('weilink-platform-branding', onBranding);
  }, [mode]);
  const brandLogo = mode === 'super' ? platformLogo : (tenant?.logoUrl || tenant?.companyLogoUrl || '');
  const initials = useMemo(() => (profile?.name || profile?.email || 'WS').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase(), [profile]);

  return (
    <div className={`app-shell ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className={`sidebar ${mobileOpen ? 'sidebar-mobile-open' : ''}`}>
        <div className="sidebar-brand">
          <div className={`brand-mark ${brandLogo ? 'has-logo' : ''}`}>{brandLogo ? <img src={brandLogo} alt={mode === 'super' ? 'Weilink Signage logo' : `${tenant?.companyName || 'Company'} logo`} /> : <Zap size={19} fill="currentColor" />}</div>
          {!collapsed && <div><strong>Weilink Signage</strong><small>{mode === 'super' ? 'Platform Console' : 'Digital Signage'}</small></div>}
          <button className="sidebar-mobile-close" onClick={() => setMobileOpen(false)}><X size={18} /></button>
        </div>

        {!collapsed && mode === 'tenant' && (
          <div className="tenant-chip">
            <div className="tenant-chip-top"><span>{tenant?.companyName || 'Your company'}</span><Badge tone={subscription?.isActive ? 'success' : subscription?.status === 'expired' ? 'danger' : 'warning'}>{subscription?.status || 'inactive'}</Badge></div>
            <div className="tenant-meter"><span style={{ width: subscription?.maxScreens ? `${Math.min(100, ((subscription.usedScreens || 0) / Math.max(1, subscription.maxScreens >= 999 ? 100 : subscription.maxScreens)) * 100)}%` : '0%' }} /></div>
            <small>{subscription?.usedScreens || 0} / {screenLimitLabel(subscription?.maxScreens || 0)} screens</small>
          </div>
        )}

        <nav className="sidebar-nav">
          {nav.map(([label, path, Icon], index) => (
            <NavLink key={path} to={path} className={({ isActive }) => `nav-item nav-tone-${index % 7} ${isActive ? 'active' : ''}`} title={collapsed ? label : undefined}>
              <Icon size={19} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <button className="nav-item sidebar-collapse" onClick={() => setCollapsed(value => !value)}>
            {collapsed ? <ChevronRight size={19} /> : <ChevronLeft size={19} />}<span>{collapsed ? 'Expand' : 'Collapse'}</span>
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />}

      <main className="main-shell">
        <header className="topbar">
          <div className="topbar-left"><button className="mobile-menu" onClick={() => setMobileOpen(true)}><Menu size={20} /></button><div className="live-pill"><span className="live-dot" /> Platform connected</div></div>
          <div className="topbar-right">
            <div className="topbar-health"><Activity size={17} /><span>Live sync</span></div>
            <div className="user-menu"><div className="avatar">{initials}</div><div className="user-copy"><strong>{profile?.name || 'Administrator'}</strong><small>{mode === 'super' ? 'Super Administrator' : profile?.role || 'Owner'}</small></div></div>
            <button className="icon-btn" onClick={logout} title="Sign out"><LogOut size={18} /></button>
          </div>
        </header>
        <div className="page-container"><Outlet /></div>
      </main>
    </div>
  );
}
